import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useSubscription, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { Button, Spin } from 'antd';

// Запрос текущего пользователя
const GET_CURRENT_USER = gql`
  query {
    currentUser {
      id
    }
  }
`;

const SEND_SIGNAL = gql`
  mutation SendCallSignal($toUserId: ID!, $signal: CallSignalInput!) {
    sendCallSignal(toUserId: $toUserId, signal: $signal)
  }
`;

const CALL_SIGNAL_SUB = gql`
  subscription OnCallSignal($userId: ID!) {
    callSignal(userId: $userId) {
      fromUserId
      type
      data
    }
  }
`;

export default function CallPage() {
  const { otherUserId } = useParams();
  const navigate = useNavigate();
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const pcRef = useRef();
  const [isInitiator, setIsInitiator] = useState(false);

  // Получаем текущего пользователя
  const { data: userData, loading: userLoading } = useQuery(GET_CURRENT_USER);
  const userId = userData?.currentUser?.id;

  const [sendSignal] = useMutation(SEND_SIGNAL);

  // Подписка на сигналы
  const { data: signalData } = useSubscription(CALL_SIGNAL_SUB, {
    variables: { userId },
    skip: !userId,
  });

  useEffect(() => {
    if (!userId) return;
    const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
    pcRef.current = pc;

    // Локальный поток
    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then(stream => {
        localVideoRef.current.srcObject = stream;
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
      })
      .catch(() => {});

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        sendSignal({
          variables: {
            toUserId: otherUserId,
            signal: { type: 'ICE_CANDIDATE', data: JSON.stringify(candidate) }
          }
        });
      }
    };

    pc.ontrack = ({ streams }) => {
      remoteVideoRef.current.srcObject = streams[0];
    };

    setIsInitiator(true);

    return () => {
      pc.close();
      navigate('/dashboard/users');
    };
  }, [userId, otherUserId, sendSignal, navigate]);

  // Обработка входящих сигналов
  useEffect(() => {
    if (!signalData) return;
    const { fromUserId, type, data } = signalData.callSignal;
    const pc = pcRef.current;
    const parsed = JSON.parse(data);
    switch (type) {
      case 'OFFER':
        pc.setRemoteDescription(parsed)
          .then(() => pc.createAnswer())
          .then(answer => pc.setLocalDescription(answer))
          .then(() => sendSignal({ variables: {
            toUserId: fromUserId,
            signal: { type: 'ANSWER', data: JSON.stringify(pc.localDescription) }
          }}));
        break;
      case 'ANSWER':
        pc.setRemoteDescription(parsed);
        break;
      case 'ICE_CANDIDATE':
        pc.addIceCandidate(new RTCIceCandidate(parsed));
        break;
      case 'END_CALL':
        pc.close();
        navigate('/dashboard/users');
        break;
      default:
    }
  }, [signalData, sendSignal, navigate]);

  // Инициация звонка
  useEffect(() => {
    if (isInitiator && pcRef.current) {
      pcRef.current.createOffer()
        .then(offer => pcRef.current.setLocalDescription(offer))
        .then(() => sendSignal({ variables: {
          toUserId: otherUserId,
          signal: { type: 'OFFER', data: JSON.stringify(pcRef.current.localDescription) }
        }}));
      setIsInitiator(false);
    }
  }, [isInitiator, otherUserId, sendSignal]);

  const endCall = () => {
    sendSignal({ variables: { toUserId: otherUserId, signal: { type: 'END_CALL', data: '' } }});
    pcRef.current.close();
    navigate('/dashboard/users');
  };

  if (userLoading) return <Spin />;

  return (
    <div className="flex flex-col items-center space-y-4 p-4">
      <div className="flex space-x-4">
        <video ref={localVideoRef} autoPlay muted className="w-1/2 rounded shadow" />
        <video ref={remoteVideoRef} autoPlay className="w-1/2 rounded shadow" />
      </div>
      <Button danger onClick={endCall}>Завершить звонок</Button>
    </div>
  );
}
